import {
  Badge,
  Box,
  Button,
  Container,
  Flex,
  HStack,
  Heading,
  Input,
  SimpleGrid,
  Spinner,
  Stack,
  Table,
  Text,
} from "@chakra-ui/react"
import { createFileRoute } from "@tanstack/react-router"
import { useState } from "react"
import { FiDollarSign, FiDownload } from "react-icons/fi"

import { InvoiceCard } from "@/components/molecules/InvoiceCard"
import { type ViewMode, ViewToggle } from "@/components/molecules/ViewToggle"
import RecordPaymentModal from "@/features/enrollments/components/RecordPaymentModal"
import {
  PaymentConfirmationModal,
  RejectPaymentModal,
} from "@/features/invoices/components"
import {
  type RejectConfirmationData,
  useApprovePaymentConfirmation,
  useRejectPaymentConfirmation,
  useSubmitPaymentConfirmation,
} from "@/hooks/api/useInvoiceVerification"
import { type EnrichedInvoice, type InvoiceFilters, useInvoices } from "@/hooks/api/useInvoices"
import type { InvoiceResponse } from "@/types/enrollment"
import type { InvoiceUpdate } from "@/client/types.gen"
import { useUpdateInvoice } from "@/hooks/api/useInvoices"
import { useCustomToast } from "@/hooks/useCustomToast"
import { usePermissions } from "@/hooks/usePermissions"
import { useIsMobile } from "@/hooks/useResponsive"
import { type ExportColumn, exportToExcel } from "@/utils/exportToExcel"

// Extended invoice type with verification fields (until API types are regenerated)
interface InvoiceWithVerification extends EnrichedInvoice {
  verification_status?: string | null
  rejection_reason?: string | null
  rejection_notes?: string | null
}

export const Route = createFileRoute("/_layout/invoices")({
  component: InvoicesPage,
})

function InvoicesPage() {
  const { can } = usePermissions()
  const canRead = can("invoices:read") || can("invoices:read_own")
  const canUpdate =
    can("invoices:update") || can("invoices:update_payment_status")
  // Parents have read_own but not full update permissions
  const isParentView = can("invoices:read_own") && !can("invoices:update")

  const [searchTerm, setSearchTerm] = useState("")
  const [statusFilter, setStatusFilter] = useState<string>("")
  const [selectedInvoice, setSelectedInvoice] =
    useState<EnrichedInvoice | null>(null)
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false)
  const [confirmationModalInvoice, setConfirmationModalInvoice] =
    useState<InvoiceWithVerification | null>(null)
  const [rejectModalInvoiceId, setRejectModalInvoiceId] = useState<
    string | null
  >(null)

  // View mode: cards default for parents, table for staff
  const [viewMode, setViewMode] = useState<ViewMode>(
    isParentView ? "cards" : "table",
  )
  const isMobile = useIsMobile()
  const effectiveViewMode = isMobile ? "cards" : viewMode

  const { data, isLoading } = useInvoices({
    payment_status: (statusFilter || undefined) as InvoiceFilters["payment_status"],
  })

  const updateInvoice = useUpdateInvoice()
  const submitConfirmation = useSubmitPaymentConfirmation()
  const approvePayment = useApprovePaymentConfirmation()
  const rejectPayment = useRejectPaymentConfirmation()
  const { showSuccessToast, showErrorToast } = useCustomToast()

  if (!canRead) {
    return (
      <Container maxW="container.xl" py={{ base: 4, md: 8 }}>
        <Text>You don't have permission to view invoices.</Text>
      </Container>
    )
  }

  const invoices = data?.items ?? []

  // Filter by search term (household name, student name, email)
  const filteredInvoices = invoices.filter((invoice) => {
    if (!searchTerm) return true
    const search = searchTerm.toLowerCase()
    return (
      invoice.household_name?.toLowerCase().includes(search) ||
      invoice.student_name?.toLowerCase().includes(search) ||
      invoice.household_email?.toLowerCase().includes(search)
    )
  })

  const handleRecordPayment = (invoice: EnrichedInvoice) => {
    setSelectedInvoice(invoice)
    setIsPaymentModalOpen(true)
  }

  const handlePaymentSubmit = async (
    _enrollmentId: string,
    paymentData: {
      amount_paid: number
      payment_method: string
      payment_date: string
      notes: string
      external_payment_id?: string
    },
  ) => {
    try {
      if (!selectedInvoice?.id) {
        showErrorToast("Invoice not found")
        return
      }

      const currentlyPaid = selectedInvoice.amount_paid ?? 0
      const newTotalPaid = currentlyPaid + paymentData.amount_paid

      await updateInvoice.mutateAsync({
        invoiceId: selectedInvoice.id,
        invoiceData: {
          amount_paid: newTotalPaid,
          payment_method: paymentData.payment_method as InvoiceUpdate["payment_method"],
          payment_date: paymentData.payment_date,
          external_payment_id: paymentData.external_payment_id || null,
          notes: paymentData.notes || selectedInvoice.notes || null,
        },
      })

      showSuccessToast(
        `Successfully recorded payment of $${paymentData.amount_paid.toFixed(2)}`,
      )
      setIsPaymentModalOpen(false)
      setSelectedInvoice(null)
    } catch (error) {
      showErrorToast("Failed to record payment. Please try again.")
    }
  }

  const getStatusBadge = (status: string) => {
    const statusColors: Record<string, string> = {
      paid: "green",
      partial: "orange",
      pending: "red",
      refunded: "gray",
    }
    return (
      <Badge colorPalette={statusColors[status] || "gray"}>
        {status.toUpperCase()}
      </Badge>
    )
  }

  const formatCurrency = (amount: number | null | undefined) => {
    if (amount === null || amount === undefined) return "$0.00"
    return `$${Number(amount).toFixed(2)}`
  }

  const getOutstandingBalance = (invoice: EnrichedInvoice) => {
    const due = invoice.amount_due ?? 0
    const paid = invoice.amount_paid ?? 0
    return due - paid
  }

  const invoiceExportColumns: ExportColumn<EnrichedInvoice>[] = [
    { header: "Household", accessor: (inv) => inv.household_name },
    { header: "Email", accessor: (inv) => inv.household_email },
    { header: "Student", accessor: (inv) => inv.student_name },
    {
      header: "Amount Due",
      accessor: (inv) =>
        inv.amount_due != null ? Number(inv.amount_due).toFixed(2) : "0.00",
    },
    {
      header: "Amount Paid",
      accessor: (inv) =>
        inv.amount_paid != null ? Number(inv.amount_paid).toFixed(2) : "0.00",
    },
    {
      header: "Balance",
      accessor: (inv) => {
        const balance = (inv.amount_due ?? 0) - (inv.amount_paid ?? 0)
        return balance.toFixed(2)
      },
    },
    { header: "Status", accessor: (inv) => inv.payment_status ?? "pending" },
    { header: "Payment Method", accessor: (inv) => inv.payment_method },
    { header: "Payment Date", accessor: (inv) => inv.payment_date },
    { header: "External ID", accessor: (inv) => inv.external_payment_id },
    { header: "Notes", accessor: (inv) => inv.notes },
  ]

  const handleExportInvoices = () => {
    const today = new Date().toISOString().slice(0, 10)
    exportToExcel(
      filteredInvoices,
      invoiceExportColumns,
      `invoices-${today}`,
      "Invoices",
    )
  }

  const getVerificationBadge = (
    verificationStatus: string | null | undefined,
  ) => {
    if (!verificationStatus) return null
    const statusConfig: Record<string, { color: string; label: string }> = {
      pending_verification: { color: "yellow", label: "Pending Verification" },
      approved: { color: "green", label: "Verified" },
      rejected: { color: "red", label: "Rejected" },
    }
    const config = statusConfig[verificationStatus]
    if (!config) return null
    return <Badge colorPalette={config.color}>{config.label}</Badge>
  }

  return (
    <Container maxW="container.xl" py={{ base: 4, md: 8 }}>
      <Stack gap={6}>
        {/* Header */}
        <Flex
          direction={{ base: "column", md: "row" }}
          justify="space-between"
          align={{ base: "stretch", md: "center" }}
          gap={{ base: 2, md: 0 }}
        >
          <Box>
            <Heading size="lg">Invoices & Payments</Heading>
            <Text color="fg.muted" mt={1}>
              {isParentView
                ? "View and manage your payments"
                : "Manage and record payments for all enrollments"}
            </Text>
          </Box>
        </Flex>

        {/* Filters */}
        <Flex
          direction={{ base: "column", md: "row" }}
          gap={{ base: 3, md: 4 }}
          align={{ base: "stretch", md: "center" }}
        >
          <Box flex="1">
            <Input
              placeholder="Search by household, student, or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="lg"
              minH="44px"
            />
          </Box>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={{
              padding: "0.5rem 1rem",
              borderRadius: "0.375rem",
              border: "1px solid var(--chakra-colors-border-default)",
              fontSize: "1rem",
              minHeight: "44px",
              width: isMobile ? "100%" : "200px",
            }}
          >
            <option value="">All Status</option>
            <option value="pending">Unpaid</option>
            <option value="partial">Partial</option>
            <option value="paid">Paid</option>
            <option value="pending_verification">Pending Verification</option>
          </select>
          {/* ViewToggle + Export - hidden on mobile since mobile always shows cards */}
          <HStack display={{ base: "none", md: "flex" }} gap={2}>
            <ViewToggle value={viewMode} onChange={setViewMode} />
            {!isParentView && (
              <Button
                size="sm"
                variant="outline"
                onClick={handleExportInvoices}
                disabled={filteredInvoices.length === 0}
              >
                <HStack gap={1}>
                  <FiDownload />
                  <Text>Export</Text>
                </HStack>
              </Button>
            )}
          </HStack>
        </Flex>

        {/* Invoice content */}
        {isLoading ? (
          <Flex justify="center" align="center" p={8}>
            <Spinner size="lg" />
          </Flex>
        ) : filteredInvoices.length === 0 ? (
          <Box borderWidth="1px" borderRadius="lg">
            <Flex
              direction="column"
              align="center"
              justify="center"
              p={8}
              gap={2}
            >
              <FiDollarSign size={48} opacity={0.3} />
              <Text fontSize="lg" fontWeight="medium">
                No invoices found
              </Text>
              <Text color="fg.muted" fontSize="sm">
                {searchTerm || statusFilter
                  ? "Try adjusting your filters"
                  : "Invoices will appear here once enrollments are created"}
              </Text>
            </Flex>
          </Box>
        ) : effectiveViewMode === "cards" ? (
          /* Card View */
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} gap={4}>
            {filteredInvoices.map((invoice) => (
              <InvoiceCard
                key={invoice.id}
                invoice={invoice as InvoiceWithVerification}
                isParentView={isParentView}
                canUpdate={canUpdate}
                onRecordPayment={handleRecordPayment}
                onConfirmPayment={(inv) =>
                  setConfirmationModalInvoice(inv as InvoiceWithVerification)
                }
                onApprove={(id) => approvePayment.mutateAsync(id)}
                onReject={(id) => setRejectModalInvoiceId(id)}
                isApproving={approvePayment.isPending}
              />
            ))}
          </SimpleGrid>
        ) : (
          /* Table View */
          <Box borderWidth="1px" borderRadius="lg" overflow="hidden">
            <Table.Root variant="outline" size="lg">
              <Table.Header>
                <Table.Row>
                  <Table.ColumnHeader>Household</Table.ColumnHeader>
                  <Table.ColumnHeader>Student</Table.ColumnHeader>
                  <Table.ColumnHeader textAlign="right">
                    Amount Due
                  </Table.ColumnHeader>
                  <Table.ColumnHeader textAlign="right">
                    Paid
                  </Table.ColumnHeader>
                  <Table.ColumnHeader textAlign="right">
                    Balance
                  </Table.ColumnHeader>
                  <Table.ColumnHeader>Status</Table.ColumnHeader>
                  <Table.ColumnHeader>Actions</Table.ColumnHeader>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {filteredInvoices.map((invoice) => {
                  const balance = getOutstandingBalance(invoice)
                  return (
                    <Table.Row key={invoice.id}>
                      <Table.Cell>
                        <Box>
                          <Text fontWeight="medium">
                            {invoice.household_name}
                          </Text>
                          <Text fontSize="sm" color="fg.muted">
                            {invoice.household_email}
                          </Text>
                        </Box>
                      </Table.Cell>
                      <Table.Cell>
                        <Text>{invoice.student_name}</Text>
                      </Table.Cell>
                      <Table.Cell textAlign="right">
                        <Text fontWeight="semibold">
                          {formatCurrency(invoice.amount_due)}
                        </Text>
                      </Table.Cell>
                      <Table.Cell textAlign="right">
                        <Text color="green.600" fontWeight="medium">
                          {formatCurrency(invoice.amount_paid)}
                        </Text>
                      </Table.Cell>
                      <Table.Cell textAlign="right">
                        <Text
                          fontWeight="bold"
                          color={balance > 0 ? "red.600" : "green.600"}
                        >
                          {formatCurrency(balance)}
                        </Text>
                      </Table.Cell>
                      <Table.Cell>
                        <Stack gap={1}>
                          {getStatusBadge(invoice.payment_status || "pending")}
                          {getVerificationBadge(
                            (invoice as InvoiceWithVerification)
                              .verification_status,
                          )}
                        </Stack>
                      </Table.Cell>
                      <Table.Cell>
                        <HStack gap={2}>
                          {/* Staff: Record Payment button */}
                          {canUpdate && balance > 0 && (
                            <Button
                              size="sm"
                              colorPalette="blue"
                              onClick={() => handleRecordPayment(invoice)}
                            >
                              Record Payment
                            </Button>
                          )}
                          {/* Parent: Confirm Payment button */}
                          {isParentView &&
                            balance > 0 &&
                            (invoice as InvoiceWithVerification)
                              .verification_status !==
                              "pending_verification" && (
                              <Button
                                size="sm"
                                colorPalette="green"
                                onClick={() =>
                                  setConfirmationModalInvoice(
                                    invoice as InvoiceWithVerification,
                                  )
                                }
                              >
                                Confirm Payment
                              </Button>
                            )}
                          {/* Staff: Approve/Reject buttons for pending verifications */}
                          {canUpdate &&
                            (invoice as InvoiceWithVerification)
                              .verification_status ===
                              "pending_verification" && (
                              <>
                                <Button
                                  size="sm"
                                  colorPalette="green"
                                  onClick={() =>
                                    approvePayment.mutateAsync(invoice.id)
                                  }
                                  loading={approvePayment.isPending}
                                >
                                  Approve
                                </Button>
                                <Button
                                  size="sm"
                                  colorPalette="red"
                                  onClick={() =>
                                    setRejectModalInvoiceId(invoice.id)
                                  }
                                >
                                  Reject
                                </Button>
                              </>
                            )}
                          {balance <= 0 && (
                            <Text
                              fontSize="sm"
                              color="green.600"
                              fontWeight="medium"
                            >
                              Paid in Full
                            </Text>
                          )}
                        </HStack>
                      </Table.Cell>
                    </Table.Row>
                  )
                })}
              </Table.Body>
            </Table.Root>
          </Box>
        )}

        {/* Summary */}
        <HStack
          justify="space-between"
          p={4}
          bg="bg.muted"
          borderRadius="md"
          flexDirection={{ base: "column", md: "row" }}
          gap={{ base: 1, md: 0 }}
        >
          <Text fontSize="sm" color="fg.muted">
            Showing {filteredInvoices.length} of {invoices.length} invoices
          </Text>
        </HStack>
      </Stack>

      {/* Payment Modal */}
      {selectedInvoice && (
        <RecordPaymentModal
          isOpen={isPaymentModalOpen}
          enrollment={{
            id: selectedInvoice.enrollment_id || "",
            student_id: selectedInvoice.student_id || "",
            class_template_id: "",
            session_id: "",
            status: "confirmed",
            effective_status: "confirmed",
            notes: null,
            created_at: selectedInvoice.created_at || "",
            updated_at: selectedInvoice.updated_at || "",
            created_by: selectedInvoice.created_by || "",
            updated_by: selectedInvoice.updated_by || "",
            invoice: selectedInvoice as InvoiceResponse,
          }}
          onClose={() => {
            setIsPaymentModalOpen(false)
            setSelectedInvoice(null)
          }}
          onSubmit={handlePaymentSubmit}
          isSubmitting={updateInvoice.isPending}
        />
      )}

      {/* Payment Confirmation Modal (for parents) */}
      <PaymentConfirmationModal
        isOpen={!!confirmationModalInvoice}
        invoice={
          confirmationModalInvoice
            ? {
                id: confirmationModalInvoice.id,
                amount_due: confirmationModalInvoice.amount_due ?? 0,
                amount_paid: confirmationModalInvoice.amount_paid ?? 0,
                verification_status:
                  confirmationModalInvoice.verification_status ?? null,
                rejection_reason:
                  confirmationModalInvoice.rejection_reason ?? null,
                rejection_notes:
                  confirmationModalInvoice.rejection_notes ?? null,
              }
            : null
        }
        onClose={() => setConfirmationModalInvoice(null)}
        onSubmit={async (invoiceId, data) => {
          await submitConfirmation.mutateAsync({ invoiceId, data })
          setConfirmationModalInvoice(null)
        }}
        isSubmitting={submitConfirmation.isPending}
      />

      {/* Reject Payment Modal (for staff) */}
      <RejectPaymentModal
        isOpen={!!rejectModalInvoiceId}
        invoiceId={rejectModalInvoiceId}
        onClose={() => setRejectModalInvoiceId(null)}
        onSubmit={async (invoiceId, data) => {
          await rejectPayment.mutateAsync({
            invoiceId,
            data: {
              rejection_reason: data.rejection_reason as RejectConfirmationData["rejection_reason"],
              rejection_notes: data.rejection_notes || undefined,
            },
          })
          setRejectModalInvoiceId(null)
        }}
        isSubmitting={rejectPayment.isPending}
      />
    </Container>
  )
}
